/**
 * Third-party Plugin Imports and Global Initialization
 * Bootstrap 5 + Vite Migration
 */

// Import styles (Vite will process these)
import '../scss/styles.scss';

// Bootstrap 5 core
import * as bootstrap from 'bootstrap';

// jQuery and plugins
import $ from 'jquery';

// Make jQuery available globally FIRST before any plugins load
window.$ = window.jQuery = $;

import 'jquery-migrate';

// Mute jQuery migrate warnings
$.migrateMute = true;

// pwstrength-bootstrap - jQuery plugin for password strength
// Load it immediately and expose a promise so registration() can wait for it
window.pwstrengthReady = import('pwstrength-bootstrap/dist/pwstrength-bootstrap.js');

// JSZip for Excel export (must be loaded BEFORE DataTables Buttons)
import JSZip from 'jszip';
window.JSZip = JSZip;

// pdfMake for PDF export (must be loaded BEFORE DataTables Buttons)
import pdfMake from 'pdfmake/build/pdfmake';
import vfsFonts from 'pdfmake/build/vfs_fonts';
// pdfmake 0.3: vfs_fonts exports the font map directly, registered via addVirtualFileSystem
pdfMake.addVirtualFileSystem(vfsFonts.default || vfsFonts);
window.pdfMake = pdfMake;

// DataTables (must be imported after jQuery)
import DataTable from 'datatables.net-bs5';

// DataTables CSS
import 'datatables.net-bs5/css/dataTables.bootstrap5.css';
import 'datatables.net-buttons-bs5/css/buttons.bootstrap5.css';
import 'datatables.net-responsive-bs5/css/responsive.bootstrap5.css';

// DataTables Buttons - ensure side-effect imports register the extensions
import 'datatables.net-buttons-bs5';
import 'datatables.net-buttons/js/buttons.html5.mjs';
import 'datatables.net-buttons/js/buttons.print.mjs';
import 'datatables.net-buttons/js/buttons.colVis.mjs';

// DataTables Responsive
import 'datatables.net-responsive';
import 'datatables.net-responsive-bs5';

// Third-party libraries
import select2 from 'select2';
import 'select2/dist/css/select2.css';
import 'select2-bootstrap-5-theme/dist/select2-bootstrap-5-theme.css';
import Swal from 'sweetalert2';
import flatpickr from 'flatpickr';
import * as LitepickerModule from 'litepicker';
// Litepicker je UMD paket; Vite 8 (Rolldown) njegovega privzetega izvoza ne preslika v razred,
// zato vzamemo poimenovani izvoz (s pomožnima možnostma za druge preslikave)
const Litepicker = LitepickerModule.Litepicker
    || (LitepickerModule.default && (LitepickerModule.default.Litepicker || LitepickerModule.default));
window.Litepicker = Litepicker;
import { Slovenian } from 'flatpickr/dist/l10n/sl.js';
import * as d3 from 'd3';
import _ from 'lodash-es';
import Cookies from 'js-cookie';
import i18next from 'i18next';
import axios from 'axios';
import moment from 'moment';

// TinyMCE - Import core and essential assets
import tinymce from 'tinymce/tinymce';

// TinyMCE theme
import 'tinymce/themes/silver/theme';

// TinyMCE icons
import 'tinymce/icons/default/icons';

// TinyMCE models
import 'tinymce/models/dom/model';

// TinyMCE plugins
import 'tinymce/plugins/link/plugin';
import 'tinymce/plugins/lists/plugin';
import 'tinymce/plugins/code/plugin';
import 'tinymce/plugins/table/plugin';
import 'tinymce/plugins/wordcount/plugin';
import 'tinymce/plugins/fullscreen/plugin';
// Note: paste functionality is built into TinyMCE core, no plugin needed

// TinyMCE Slovenian language
import 'tinymce-i18n/langs8/sl-SI.js';

// TinyMCE Skin CSS - Import to avoid 403 errors
import 'tinymce/skins/ui/oxide/skin.min.css';
import 'tinymce/skins/ui/oxide/content.min.css';
import 'tinymce/skins/content/default/content.min.css';

// Override console.log to only work on localhost (development)
const originalConsoleLog = console.log.bind(console);
console.log = (...args) => {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        originalConsoleLog(...args);
    }
};

// Make libraries available globally for templates and legacy code (jQuery already set above)
window.DataTable = DataTable;
// DataTables 3 ne zahteva več jQueryja: integracijo ($.fn.DataTable, $.fn.dataTable) in Bootstrap 5
// je treba priklopiti izrecno, ker se ES uvozi izvedejo pred nastavitvijo window.jQuery
DataTable.use($);
DataTable.use(bootstrap);
window.select2 = select2;
window.Swal = Swal;
// Backward compatibility: map old swal() to new Swal.fire()
window.swal = (options) => Swal.fire(options);
window.flatpickr = flatpickr;
window.flatpickrLocale = { sl: Slovenian };
window.d3 = d3;
window._ = _;
window.Cookies = Cookies;
window.i18next = i18next;
window.bootstrap = bootstrap;
window.axios = axios;
window.moment = moment;
window.tinymce = tinymce;

// Preverjanje črkovanja v vseh urejevalnikih TinyMCE: TinyMCE ga privzeto izklopi (browser_spellcheck) in s svojim
// kontekstnim menijem prekrije meni brskalnika s predlogi popravkov. Vklopimo ga in vsebini nastavimo jezik strani,
// da brskalnik uporabi slovenski (oz. angleški) slovar. Slovar mora biti v brskalniku nameščen/omogočen.
const originalTinymceInit = tinymce.init.bind(tinymce);
tinymce.init = (options = {}) => {
    const userSetup = options.setup;
    return originalTinymceInit({
        ...options,
        browser_spellcheck: true,
        contextmenu: false,
        setup: (editor) => {
            editor.on('init', () => {
                const lang = document.documentElement.getAttribute('lang') || 'sl';
                editor.getDoc().documentElement.setAttribute('lang', lang);
                editor.getBody().setAttribute('lang', lang);
                editor.getBody().setAttribute('spellcheck', 'true');
            });
            if (typeof userSetup === 'function') {
                userSetup(editor);
            }
        }
    });
};

// TinyMCE is now loaded via Vite (see imports above)
// TinyMCE is immediately available - no need for dynamic loading like CKEditor
console.log('TinyMCE loaded and ready');

// For backward compatibility with code that checks for CKEditorReady
// This promise resolves immediately since TinyMCE is already loaded
window.TinyMCEReady = Promise.resolve();
window.CKEditorReady = Promise.resolve(); // Legacy compatibility

// DataTables initialization - resolve the loader promise that templates expect
window.DataTablesLoader = Promise.resolve();

// Configure axios defaults
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// Notify classic scripts that jQuery is ready on the global scope.
document.addEventListener('DOMContentLoaded', function () {
  window.dispatchEvent(new Event('jquery-ready'));
});

// Provide jQuery-style bridges for Bootstrap 5 components used in legacy code.
function bridgeBootstrapPlugin(pluginName, BootstrapClass, methodMap) {
  if (!$.fn || $.fn[pluginName]) {
    return;
  }

  $.fn[pluginName] = function (config, ...args) {
    return this.each(function () {
      var instance = BootstrapClass.getInstance(this);

      if (typeof config === 'string') {
        var method = (methodMap && methodMap[config]) || config;
        if (instance && typeof instance[method] === 'function') {
          instance[method](...args);
        }
        return;
      }

      var options = config || {};
      instance = BootstrapClass.getOrCreateInstance(this, options);

      // Bootstrap 3 would auto-show modal/tooltip/popover unless show:false supplied.
      var shouldShow = options.show;
      if (shouldShow === undefined) {
        shouldShow = pluginName === 'modal';
      }

      if (shouldShow && typeof instance.show === 'function') {
        instance.show();
      }
    });
  };
}

bridgeBootstrapPlugin('modal', bootstrap.Modal, { hide: 'hide', show: 'show' });
bridgeBootstrapPlugin('tooltip', bootstrap.Tooltip, { destroy: 'dispose' });
bridgeBootstrapPlugin('popover', bootstrap.Popover, { destroy: 'dispose' });

// Initialize Select2 globally
select2($);

// Bootstrap 5 Auto-initialization
document.addEventListener('DOMContentLoaded', () => {
  // Auto-initialize all tooltips
  const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
  [...tooltipTriggerList].forEach(tooltipTriggerEl => {
    new bootstrap.Tooltip(tooltipTriggerEl);
  });

  // Auto-initialize all popovers
  const popoverTriggerList = document.querySelectorAll('[data-bs-toggle="popover"]');
  [...popoverTriggerList].forEach(popoverTriggerEl => {
    new bootstrap.Popover(popoverTriggerEl);
  });

  // Load jQuery plugins after DOM is ready and jQuery is globally available
  Promise.all([
    import('jquery.loadtemplate/dist/jquery.loadTemplate.js'),
    import('geocomplete/jquery.geocomplete.js'),
    import('print-this').then(module => {
      // Explicitly initialize print-this with jQuery
      if (module.default && typeof module.default === 'function') {
        module.default($);
      }
    })
  ]).then(() => {
    console.log('jQuery plugins (loadtemplate, geocomplete, print-this) loaded');
    // Verify printThis method is available
    if (typeof $.fn.printThis === 'function') {
      console.log('$.fn.printThis is available');
    } else {
      console.error('$.fn.printThis is NOT available');
    }
    // Dispatch event to notify that jQuery plugins are ready
    window.dispatchEvent(new Event('jquery-plugins-ready'));
  }).catch(err => {
    console.error('Failed to load jQuery plugins:', err);
  });
});

// Create a promise that resolves when jQuery plugins are loaded
window.jQueryPluginsReady = new Promise((resolve) => {
  window.addEventListener('jquery-plugins-ready', resolve, { once: true });
});

// Export for module usage
export { $, select2, Swal, flatpickr, d3, _, Cookies, i18next, bootstrap, tinymce, Litepicker };
